import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import KitchenIcon from '@mui/icons-material/Kitchen';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArchiveIcon from '@mui/icons-material/Archive';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const API_URL = import.meta.env.VITE_API_URL;

const statusLabels = {
  draft: 'Borrador',
  ready: 'Listo',
  validated: 'Validado',
  applied: 'Aplicado',
  cooking: 'En cocina',
  completed: 'Completado',
  archived: 'Archivado',
};

const BatchPlanDetailPage = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const { authToken } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState('');
  const [error, setError] = useState('');

  const proposal = plan?.proposal;
  const status = plan?.status;
  const canCook = ['ready', 'applied', 'cooking', 'completed'].includes(status);

  const fetchPlan = useCallback(async () => {
    if (!authToken || !planId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals/${planId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo cargar el plan');
      setPlan(payload.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authToken, planId]);

  useEffect(() => {
    fetchPlan();
  }, [fetchPlan]);

  const recipeName = (id) => proposal?.recipeDetails?.[id]?.name || id;

  const handleMarkCooked = async (recipeIds) => {
    if (!planId) return;
    setBusyAction(recipeIds?.length ? `cook:${recipeIds[0]}` : 'cook-all');
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals/${planId}/mark-cooked`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(recipeIds?.length ? { recipeIds } : {}),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo marcar como cocinado');
      await fetchPlan();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction('');
    }
  };

  const handleArchive = async () => {
    if (!planId) return;
    setBusyAction('archive');
    setError('');
    try {
      const response = await fetch(`${API_URL}/batch-planning/proposals/${planId}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.message || 'No se pudo archivar el plan');
      navigate('/menu-semanal');
    } catch (err) {
      setError(err.message);
      setBusyAction('');
    }
  };

  const menuByDate = useMemo(() => {
    const grouped = {};
    for (const entry of proposal?.menuProposal || []) {
      if (!grouped[entry.date]) grouped[entry.date] = [];
      grouped[entry.date].push(entry);
    }
    return grouped;
  }, [proposal]);

  if (loading) {
    return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;
  }

  return (
    <Container className="modern-resource-page" maxWidth="xl" sx={{ py: 4, pb: 10 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/menu-semanal')} sx={{ mb: 2 }}>
        Volver al planificador
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!proposal && !loading && (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <KitchenIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" fontWeight={800}>Plan no disponible</Typography>
        </Paper>
      )}

      {proposal && (
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="h5" fontWeight={800}>
                    Batch cooking · {proposal.start} – {proposal.end}
                  </Typography>
                  {proposal.strategy?.aiBrain && <Chip size="small" color="primary" icon={<AutoAwesomeIcon />} label="IA" />}
                </Box>
                <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
                  <Chip label={statusLabels[status] || status} />
                  <Chip label={`${proposal.metrics?.prepBlocksCount || 0} preparaciones`} />
                  <Chip label={`${proposal.metrics?.estimatedActiveMinutes || 0} min activos`} />
                  <Chip label={`${proposal.metrics?.stockCreatedPortions || 0} raciones Fondo`} />
                </Stack>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  variant="contained"
                  startIcon={busyAction === 'cook-all' ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                  onClick={() => handleMarkCooked()}
                  disabled={!canCook || busyAction === 'cook-all'}
                >
                  Marcar todo cocinado
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={busyAction === 'archive' ? <CircularProgress size={18} /> : <ArchiveIcon />}
                  onClick={handleArchive}
                  disabled={busyAction === 'archive' || status === 'archived'}
                >
                  Archivar
                </Button>
              </Stack>
            </Box>
            <Box mt={2}>
              <Typography variant="caption" color="text.secondary">Reutilización de ingredientes</Typography>
              <LinearProgress variant="determinate" value={proposal.metrics?.ingredientReuseScore || 0} sx={{ mt: 0.5 }} />
            </Box>
            {proposal.aiCookingSession?.summary && (
              <Alert severity="info" icon={<AutoAwesomeIcon />} sx={{ mt: 2 }}>
                {proposal.aiCookingSession.summary}
              </Alert>
            )}
            {(proposal.warnings || []).length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>{proposal.warnings.join(' ')}</Alert>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Preparaciones comunes</Typography>
            <Grid container spacing={2}>
              {(proposal.prepBlocks || []).map((block) => (
                <Grid item xs={12} md={6} key={block.key}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography fontWeight={800}>{block.label}</Typography>
                      {block.estimatedMinutes > 0 && <Chip size="small" label={`${block.estimatedMinutes} min`} />}
                    </Box>
                    {block.reason && <Typography variant="body2" color="text.secondary">{block.reason}</Typography>}
                    <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap">
                      {(block.sharedIngredients || []).slice(0, 6).map((ingredient) => (
                        <Chip key={ingredient.id || ingredient.name} size="small" variant="outlined" label={ingredient.name} />
                      ))}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Recetas: {(block.recipes || []).map((recipe) => recipe.name).join(', ')}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              {(proposal.prepBlocks || []).length === 0 && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">No se han detectado preparaciones comunes claras.</Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {(proposal.notBatchable || []).length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <WarningAmberIcon color="warning" />
                <Typography variant="h6" fontWeight={800}>No recomendado para batch</Typography>
              </Box>
              <Stack spacing={1}>
                {(proposal.notBatchable || []).map((item, index) => (
                  <Box key={`${item.recipeId || item.name}-${index}`}>
                    <Typography variant="body2" fontWeight={700}>
                      {item.name || recipeName(item.recipeId) || 'Receta'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{item.reason}</Typography>
                  </Box>
                ))}
              </Stack>
            </Paper>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Raciones para Fondo</Typography>
            <Stack spacing={1}>
              {(proposal.stockPreparations || []).map((prep) => (
                <Paper key={`${prep.recipe}-${prep.scheduledFrom?.date}`} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap">
                    <Box>
                      <Typography fontWeight={800}>{prep.recipeName || recipeName(prep.recipe)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {prep.portions} raciones · {prep.storage === 'freezer' ? 'Congelador' : 'Nevera'}
                        {prep.scheduledFrom?.date ? ` · desde ${prep.scheduledFrom.date} ${prep.scheduledFrom.meal}` : ''}
                      </Typography>
                      {prep.cookedAt && <Chip size="small" color="success" label="Registrado en Fondo" sx={{ mt: 0.5 }} />}
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleMarkCooked([prep.recipe])}
                      disabled={!canCook || !!prep.cookedAt || busyAction === `cook:${prep.recipe}`}
                    >
                      Registrar esta receta
                    </Button>
                  </Box>
                </Paper>
              ))}
              {(proposal.stockPreparations || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">Este plan no propone raciones extra para Fondo.</Typography>
              )}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Sesión de cocina</Typography>
            {(proposal.cookSessions || []).map((session) => (
              <Box key={session.date} mb={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography fontWeight={800}>{session.title} · {session.date}</Typography>
                  {session.activeMinutes > 0 && <Chip size="small" label={`${session.activeMinutes} min activos`} />}
                </Box>
                {session.overview && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{session.overview}</Typography>
                )}
                <Stack divider={<Divider flexItem />} spacing={1} mt={1}>
                  {(session.tasks || []).map((task, index) => (
                    <Box key={`${task.type}-${index}`} display="flex" gap={1}>
                      <RestaurantIcon sx={{ color: 'primary.main', fontSize: 18, mt: 0.2 }} />
                      <Box>
                        <Typography variant="body2" fontWeight={700}>
                          {task.label}
                          {task.estimatedMinutes > 0 && (
                            <Typography component="span" variant="caption" color="text.secondary"> · {task.estimatedMinutes} min</Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{(task.instructions || []).join(' ')}</Typography>
                        {(task.recipeIds || []).length > 0 && (
                          <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                            {task.recipeIds.map((id) => (
                              <Chip
                                key={id}
                                size="small"
                                variant="outlined"
                                clickable
                                label={recipeName(id)}
                                onClick={() => navigate(`/recetas/${id}`)}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            ))}
            {(proposal.cookSessions || []).length === 0 && (
              <Typography variant="body2" color="text.secondary">Sin sesión de cocina generada.</Typography>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>Menú del plan</Typography>
            <Grid container spacing={2}>
              {Object.entries(menuByDate).map(([date, entries]) => (
                <Grid item xs={12} sm={6} md={4} key={date}>
                  <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, height: '100%' }}>
                    <Typography fontWeight={800} mb={1}>{date}</Typography>
                    <Stack divider={<Divider flexItem />} spacing={1}>
                      {entries.map((entry) => (
                        <Box key={`${date}-${entry.meal}`}>
                          <Box display="flex" justifyContent="space-between" gap={1}>
                            <Typography variant="body2" fontWeight={700}>{entry.meal}</Typography>
                            {entry.source === 'stock' && <Chip size="small" icon={<Inventory2Icon />} label="Fondo" />}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {(entry.recipes || []).map(recipeName).join(', ') || 'Sin receta'}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Stack>
      )}
    </Container>
  );
};

export default BatchPlanDetailPage;
